import { hexToWorld, ROW, worldToHex } from '../hex.ts';
import { cosA, idiv, imax, mulBp, sinA, withinRange, dist2 } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, LitterRules, LitterState, PasState, SendaNpc } from '../types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { plankAt, walkLit } from './pasarela-layout.ts';
import { sendaCue } from './senda.ts';

/**
 * The cleric's last road, chapter 2 (docs/sendas/clerigo.md, «La pasarela»; grounds in battle/pasarela-layout.ts,
 * numbers in content CAMINO.ridge, walk, bridge). The stretcher's own rule (battle/litter.ts) carries it; this adds what
 * each act does on top, through five hooks litter.ts calls: `pasHalt` (it doesn't go on), `pasBlocked` (not onto that),
 * `pasArrived` (a point of its way reached), `pasTick` and `pasHold` (its nightmares).
 *
 * - The cliff path (`ridge`): the stretcher waits in each chapel until the hero says go (the mark on it). The slingers on
 *   the ledge across the fog, out of reach, throw a volley every so often; its mark shows under the hero a while before.
 *   In a chapel's shadow (or a sanctuary) the stones don't reach; out of it, the three take their blows and the hero in
 *   its mark its share. Gargoyles come off the rock at the carriers.
 * - The walkway (`walk`, §4 «La luna en la pasarela»): the moon by the first post, a mirror on each post (down, on,
 *   back); the span of the first post down is lit if every post before it is on. A lit span is clean of ink after its
 *   burn; a dark one is covered again a while after. The carriers only step on clean whole planks; the hero on ink or a
 *   torn plank falls to the nets and climbs back at the span's post. Piet slips on his span and hangs until the hero gets
 *   him up, or a gargoyle lets him go. Gargoyles land on planks ahead and tear them; the hero beside one lays it again.
 * - The chapels' bridge (`bridge`, the test): the stretcher goes when the hero says go and waits when told; never onto a
 *   torn plank, and over one too long it falls. The bone mangonel on the far chapel throws at where the stretcher will
 *   be (its shadow first): each stone breaks two planks. Gargoyles tear planks by it; the limpets climb out of the fog.
 *
 * Cost: a handful of nightmares and twenty planks, looked at once a tick; nothing on the board is searched twice.
 */

/** The mark on the stretcher: go (or wait, on the bridge). The walkway's posts are marks `POST_MARK + i`. */
export const LITTER_GO = 10;
export const POST_MARK = 2;
const NEVER = 0x7fffffff;

const person = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);
const scaled = (s: BattleState, e: Enemy): Enemy => {
  if (s.rules?.senda?.litter && s.rules.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  return e;
};

const fresh = (r: LitterRules): PasState => {
  const W = r.walk;
  const B = r.span;
  const planks = W ? W.spans.reduce((n, sp) => n + sp.length, 0) : B ? B.planks.length : 0;
  const z: number[] = [];
  for (let i = 0; i < planks; i++) z.push(0);
  return {
    wait: r.ridge ? 0 : -1,
    passed: -1,
    go: false,
    volley: r.ridge?.volley.first ?? NEVER,
    marks: [],
    gar: r.ridge?.gargoyle.first ?? W?.gargoyle.first ?? B?.gargoyle.first ?? NEVER,
    lit: -1,
    clean: W ? W.spans.map(() => 0) : [],
    dark: W ? W.spans.map(() => 0) : [],
    torn: z,
    perch: [],
    mend: -1,
    mendT: 0,
    slip: -1,
    slipT: 0,
    hang: 0,
    boss: 0,
    stone: B?.mangonel.first ?? NEVER,
    stones: [],
    falls: 0,
  };
};

/** A blow on one of the stretcher's three (Tessa's in ten-thousandths, as litter.ts settles them). */
const blowOn = (s: BattleState, w: LitterState, n: SendaNpc, blows: number, cue: (c: string) => void): void => {
  if (n.hp <= 0 || blows <= 0) return;
  if (n.id === 'tessa') {
    w.acc += blows * 10000;
    while (w.acc >= 10000 && n.hp > 0) {
      w.acc -= 10000;
      n.hp--;
      ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
    }
    return;
  }
  n.hp = imax(0, n.hp - blows);
  ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
  if (n.hp <= 0) {
    ev(s, { t: 'senda', kind: 'set', npc: n.id, x: w.x, y: w.y, n: n.id === 'piet' ? 0 : 1 });
    cue('down1');
  }
};

/* ---------- the planks (the walkway's and the bridge's) ---------- */

/** The walkway's plank under a point (span by span from the foot), or −1 (a landing, the rock). */
export const walkPlankAt = (r: LitterRules, x: number, y: number): number => {
  const W = r.walk;
  if (!W) return -1;
  const c = worldToHex(x, y);
  let n = 0;
  for (const sp of W.spans) {
    for (let k = 0; k < sp.length; k++) {
      const h = sp[k] as { q: number; r: number };
      if (h.q === c.q && h.r === c.r) return n + k;
    }
    n += sp.length;
  }
  return -1;
};

/** The span of a walkway's plank. */
const spanOf = (r: LitterRules, id: number): number => {
  const W = r.walk;
  if (!W || id < 0) return -1;
  let n = 0;
  for (let i = 0; i < W.spans.length; i++) {
    n += (W.spans[i] as unknown[]).length;
    if (id < n) return i;
  }
  return -1;
};

/** A plank's middle (sim units). */
export const plankPoint = (r: LitterRules, id: number): { x: number; y: number } | null => {
  if (r.walk) {
    let n = 0;
    for (const sp of r.walk.spans) {
      if (id < n + sp.length) {
        const h = sp[id - n] as { q: number; r: number };
        return hexToWorld(h.q, h.r);
      }
      n += sp.length;
    }
    return null;
  }
  const k = r.span?.planks[id];
  return k ? { x: 0, y: k.r * ROW } : null;
};

/** Whether a walkway's plank can't be stepped on: torn, or its span under ink. */
const walkBad = (r: LitterRules, p: PasState, id: number): boolean => {
  if (id < 0) return false;
  if (p.torn[id]) return true;
  const sp = spanOf(r, id);
  return (p.clean[sp] ?? 0) < (r.walk?.burn ?? 0);
};

/** The plank of the bridge under a point, or −1. */
const bridgePlank = (r: LitterRules, y: number): number => (r.span ? plankAt(y) : -1);

/* ---------- the hooks litter.ts calls ---------- */

/** The stretcher doesn't go on: waiting in a chapel (the cliff path), told to wait (the bridge), Piet hanging (the walkway). */
export const pasHalt = (r: LitterRules, p: PasState): boolean => (r.ridge ? p.wait >= 0 : r.span ? !p.go : r.walk ? p.slip >= 0 : false);

/** Not onto what is ahead (its front pole there): ink or a torn plank on the walkway, a torn plank on the bridge. */
export const pasBlocked = (r: LitterRules, p: PasState, x: number, y: number, facing: number): boolean => {
  // (Piet's foot: his place at the front pole, a little past it.)
  const fx = x + idiv(cosA(facing) * 700, 10000);
  const fy = y + idiv(sinA(facing) * 700, 10000);
  if (r.walk) return walkBad(r, p, walkPlankAt(r, fx, fy));
  if (r.span) {
    const k = bridgePlank(r, fy);
    return k >= 0 && !!p.torn[k];
  }
  return false;
};

/** A point of the way reached (true: it stops there): the cliff path's chapels. */
export const pasArrived = (s: BattleState, r: LitterRules, w: LitterState, cue: (c: string) => void): boolean => {
  const R = r.ridge;
  const p = w.pas;
  if (!R || !p) return false;
  const i = R.chapels.findIndex((c) => c.at === w.seg);
  if (i < 0 || i <= p.passed) return false;
  p.wait = i;
  const c = R.chapels[i] as { x: number; y: number };
  ev(s, { t: 'senda', kind: 'pasWait', npc: '', x: c.x, y: c.y, n: i });
  if (i === 2) cue('arriero');
  return true;
};

/** Sends the hero to a post (the walkway), or tells the stretcher to go (or wait). */
export const pasOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const p = w?.pas;
  if (!r || !w || !p) return;
  if (at === LITTER_GO) {
    if (r.ridge && p.wait >= 0) {
      p.passed = p.wait;
      p.wait = -1;
      ev(s, { t: 'senda', kind: 'pasGo', npc: '', x: w.x, y: w.y, n: 1 });
      sendaCue(s, 'open');
    } else if (r.span && w.done < 0) {
      p.go = !p.go;
      ev(s, { t: 'senda', kind: 'pasGo', npc: '', x: w.x, y: w.y, n: p.go ? 1 : 0 });
    }
    return;
  }
  const W = r.walk;
  const post = W?.posts[at - POST_MARK];
  if (!W || !post) return;
  const l = W.landings[at - POST_MARK] as { q: number; r: number };
  const c = hexToWorld(l.q, l.r);
  w.job = at;
  w.held = 0;
  orderMove(s, h, c.x - 200, c.y + 150);
  h.target = 0;
  h.forced = false;
};

/** The point of a mark: the stretcher, a post. */
export const pasMarkAt = (s: BattleState, at: number): { x: number; y: number } | null => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  if (!r || !w) return null;
  if (at === LITTER_GO) return { x: w.x, y: w.y };
  const post = r.walk?.posts[at - POST_MARK];
  return post ? { x: post.x, y: post.y } : null;
};

/* ---------- the cliff path ---------- */

const sheltered = (s: BattleState, r: LitterRules, x: number, y: number): boolean => {
  const R = r.ridge;
  if (R) for (const c of R.chapels) if (withinRange(x, y, c.x, c.y, R.shade)) return true;
  for (const z of s.zones) if (z.kind === 'sanctuary' && withinRange(x, y, z.x, z.y, z.radius)) return true;
  return false;
};

const ridgeTick = (s: BattleState, r: LitterRules, w: LitterState, p: PasState, h: Hero | undefined, cue: (c: string) => void): void => {
  const R = r.ridge;
  if (!R) return;
  const ar = person(s, 'arriero');
  if (ar) {
    ar.x = R.arriero.x;
    ar.y = R.arriero.y;
    ar.facing = 0;
    ar.moving = false;
  }
  // The slingers across the fog, three, from the start.
  if (!p.boss) {
    for (let k = 0; k < 3; k++) {
      const e = scaled(s, spawnEnemy(s, 'slinger', 1, false, 0, R.ledge + k * 300, w.y - 1600 + k * 1600));
      if (k === 0) p.boss = e.id;
    }
  }
  if (w.done >= 0) return;
  const V = R.volley;
  // The volley's warning: the hero's mark where it stands (the stretcher's three are struck wherever they are).
  if (!p.marks.length && s.waveT >= p.volley - V.warn && h) {
    p.marks = [h.x, h.y];
    ev(s, { t: 'senda', kind: 'volleyWarn', npc: '', x: h.x, y: h.y, n: imax(1, p.volley - s.waveT) });
    cue('volley1');
  }
  if (s.waveT >= p.volley) {
    const open = !sheltered(s, r, w.x, w.y);
    if (open) for (const id of ['tessa', 'piet', 'ludo']) {
      const n = person(s, id);
      if (n) blowOn(s, w, n, id === 'tessa' ? V.tessa : V.blows, cue);
    }
    ev(s, { t: 'senda', kind: 'volley', npc: '', x: w.x, y: w.y, n: open ? 1 : 0 });
    const mx = p.marks[0];
    const my = p.marks[1];
    if (h && mx !== undefined && my !== undefined && withinRange(h.x, h.y, mx, my, V.r) && !sheltered(s, r, h.x, h.y)) damageHero(s, h, mulBp(h.stats.maxHp, V.heroBp), null, 'physical', true);
    p.marks = [];
    p.volley += V.every;
  }
  // Gargoyles off the rock, by the stretcher (the den ahead nearest it).
  if (s.waveT >= p.gar) {
    const G = R.gargoyle;
    p.gar = s.waveT + G.every;
    let pick: { x: number; y: number; at: number } | null = null;
    for (const d of r.dens) if (d.at >= w.seg - 1 && (!pick || d.at < pick.at)) pick = d;
    pick ??= r.dens[r.dens.length - 1] ?? null;
    if (pick) {
      const n = rngRange(s.rng, G.n[0], G.n[1]);
      for (let k = 0; k < n; k++) scaled(s, spawnEnemy(s, 'gargoyle', 1, false, 0, pick.x - k * 400, pick.y + k * 500));
    }
  }
};

/* ---------- the walkway ---------- */

const walkTick = (s: BattleState, r: LitterRules, w: LitterState, p: PasState, h: Hero | undefined, cue: (c: string) => void): void => {
  const W = r.walk;
  if (!W) return;
  if (w.turns.length !== W.posts.length) w.turns = [...W.start];
  // The moon on its span; each span's ink burnt by it, or covering again in the dark.
  const lit = walkLit(w.turns);
  if (lit !== p.lit) {
    p.lit = lit;
    const post = W.posts[imax(0, lit)] as { x: number; y: number };
    ev(s, { t: 'senda', kind: 'moon', npc: '', x: post.x, y: post.y, n: lit });
  }
  for (let i = 0; i < W.spans.length; i++) {
    const c = p.clean[i] ?? 0;
    if (i === lit) {
      p.dark[i] = 0;
      if (c < W.burn) {
        p.clean[i] = c + 1;
        if (c + 1 === W.burn) {
          const q = plankPoint(r, i * 4 + 1);
          ev(s, { t: 'senda', kind: 'clean', npc: '', x: q?.x ?? 0, y: q?.y ?? 0, n: i });
        }
      }
    } else if (c >= W.burn) {
      const d = (p.dark[i] ?? 0) + 1;
      p.dark[i] = d;
      if (d >= W.cover) {
        p.clean[i] = 0;
        p.dark[i] = 0;
        const q = plankPoint(r, i * 4 + 1);
        ev(s, { t: 'senda', kind: 'inked', npc: '', x: q?.x ?? 0, y: q?.y ?? 0, n: i });
        cue('dark');
      }
    } else p.clean[i] = 0;
  }
  if (!h) return;
  // The hero on ink or a torn plank: down to the nets, and up again at the span's post.
  const under = walkPlankAt(r, h.x, h.y);
  if (under >= 0 && walkBad(r, p, under)) {
    const sp = spanOf(r, under);
    const l = W.landings[sp] as { q: number; r: number };
    const c = hexToWorld(l.q, l.r);
    ev(s, { t: 'senda', kind: 'fall', npc: '', x: h.x, y: h.y, n: sp });
    h.x = c.x - 300;
    h.y = c.y + 200;
    h.ax = h.x;
    h.ay = h.y;
    h.moving = false;
    h.path = [];
    h.destX = h.x;
    h.destY = h.y;
    w.job = -1;
    p.falls++;
    damageHero(s, h, mulBp(h.stats.maxHp, W.fallBp), null, 'physical', true);
    cue('fall');
    // Something comes up the ladder after it.
    if (s.enemies.filter((e) => e.hp > 0 && e.kind === 'gargoyle').length < 3) scaled(s, spawnEnemy(s, 'gargoyle', 1, false, 0, c.x + 2600, c.y - 600));
  }
  // A post's mirror turned by standing by it.
  if (w.job >= POST_MARK) {
    const post = W.posts[w.job - POST_MARK];
    const l = W.landings[w.job - POST_MARK] as { q: number; r: number } | undefined;
    const c = l ? hexToWorld(l.q, l.r) : null;
    if (!post || !c || h.moving || !withinRange(h.x, h.y, c.x, c.y, W.reach)) w.held = 0;
    else if (++w.held >= W.stand) {
      const i = w.job - POST_MARK;
      w.job = -1;
      w.held = 0;
      w.turns[i] = ((w.turns[i] ?? 0) + 1) % 3;
      ev(s, { t: 'senda', kind: 'mirror', npc: '', x: post.x, y: post.y, n: i });
    }
  }
  // Aud's line by the first post.
  if (s.tick % 12 === 0) {
    const p0 = W.posts[0];
    if (p0 && withinRange(h.x, h.y, p0.x, p0.y, 2400)) cue('inscription');
  }
  // Piet's slip, halfway along his span.
  const S = W.slip;
  const at = S.span * 5 + 2;
  const piet = person(s, 'piet');
  if (p.slip === -1 && w.seg >= at && piet && piet.hp > 0) {
    p.slip = s.tick;
    p.slipT = 0;
    ev(s, { t: 'senda', kind: 'slip', npc: 'piet', x: piet.x, y: piet.y });
    cue('slip');
    const g = scaled(s, spawnEnemy(s, 'gargoyle', 1, false, 0, piet.x + 3000, piet.y - 1200));
    p.perch.push({ id: g.id, plank: -1, t: -1 });
  }
  if (p.slip >= 0 && piet) {
    if (withinRange(h.x, h.y, piet.x, piet.y, r.liftReach)) {
      if (++p.slipT >= r.lift) {
        p.slip = -2;
        ev(s, { t: 'senda', kind: 'saved', npc: 'piet', x: piet.x, y: piet.y });
        cue('saved');
      }
    } else p.slipT = 0;
    if (p.slip >= 0 && s.tick - p.slip >= S.bar) {
      p.slip = -3;
      ev(s, { t: 'senda', kind: 'dropped', npc: 'piet', x: piet.x, y: piet.y });
    }
  }
  // The moon burns what stands on its span.
  if (lit >= 0 && s.tick % 12 === 0) {
    const a = W.landings[lit] as { q: number; r: number };
    const b = W.landings[lit + 1] as { q: number; r: number };
    const A = hexToWorld(a.q, a.r);
    const B = hexToWorld(b.q, b.r);
    for (const e of s.enemies) {
      if (e.hp <= 0) continue;
      if (segD2(e.x, e.y, A.x, A.y, B.x, B.y) <= 650 * 650) damageEnemy(s, e, W.beam, { pid: '', type: 'holy' });
    }
  }
};

const segD2 = (px: number, py: number, ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? idiv(((px - ax) * dx + (py - ay) * dy) * 1000, len2) : 0;
  t = t < 0 ? 0 : t > 1000 ? 1000 : t;
  return dist2(px, py, ax + idiv(dx * t, 1000), ay + idiv(dy * t, 1000));
};

/* ---------- the chapels' bridge ---------- */

const bridgeTick = (s: BattleState, r: LitterRules, w: LitterState, p: PasState, h: Hero | undefined, cue: (c: string) => void): void => {
  const B = r.span;
  if (!B) return;
  const M = B.mangonel;
  if (!p.boss) {
    const e = spawnEnemy(s, 'mangonel', 1, false, 0, M.x, M.y, M.hp);
    e.facing = 256;
    p.boss = e.id;
  }
  const m = s.enemies.find((e) => e.id === p.boss && e.hp > 0);
  if (m) m.stun = 0;
  // Its stone, at the stretcher's way: where it will be when it lands (going on), or the plank in front of it (waiting);
  // its shadow first. It breaks that plank and the next one on.
  if (m && w.done < 0 && s.waveT >= p.stone) {
    // (A second either way: never quite the same rhythm.)
    p.stone = s.waveT + M.every + rngRange(s.rng, -12, 12);
    const k = w.moving ? imax(0, plankAt(w.y - r.pace * M.warn)) : plankAt(w.y) + 1;
    const y = (r.span?.planks[imax(0, k)]?.r ?? -8) * ROW;
    const x = rngRange(s.rng, -250, 250);
    p.stones.push({ x, y, at: s.waveT + M.warn, away: 1 });
    ev(s, { t: 'senda', kind: 'stoneWarn', npc: '', x, y, n: M.warn });
    m.anim = 'attack';
    m.animT = 0;
    cue('stone1');
  }
  for (let i = 0; i < p.stones.length; i++) {
    const st = p.stones[i] as { x: number; y: number; at: number; away: number };
    if (s.waveT < st.at) continue;
    p.stones.splice(i--, 1);
    const k0 = plankAt(st.y);
    for (const k of [k0, k0 + st.away]) {
      if (k < 0 || k >= p.torn.length || p.torn[k]) continue;
      p.torn[k] = 1;
      const q = plankPoint(r, k);
      ev(s, { t: 'senda', kind: 'tear', npc: '', x: q?.x ?? 0, y: q?.y ?? 0, n: k });
    }
    ev(s, { t: 'senda', kind: 'stone', npc: '', x: st.x, y: st.y });
    if (h && withinRange(h.x, h.y, st.x, st.y, M.r)) damageHero(s, h, mulBp(h.stats.maxHp, M.heroBp), m ?? null, 'physical', true);
    const t = person(s, 'tessa');
    if (t && withinRange(w.x, w.y, st.x, st.y, M.r)) blowOn(s, w, t, M.tessa, cue);
  }
  // Over a gap: the stretcher hangs from its poles a moment, then falls.
  const under = bridgePlank(r, w.y);
  if (under >= 0 && p.torn[under] && w.done < 0) {
    p.hang++;
    if (p.hang === 1) {
      ev(s, { t: 'senda', kind: 'overGap', npc: '', x: w.x, y: w.y });
      cue('hang');
    }
    if (p.hang === B.hang) ev(s, { t: 'senda', kind: 'gone', npc: '', x: w.x, y: w.y });
  } else p.hang = 0;
};

/* ---------- the gargoyles on the planks, the hero laying them ---------- */

const planks = (s: BattleState, r: LitterRules, w: LitterState, p: PasState, h: Hero | undefined): void => {
  const G = r.walk?.gargoyle ?? r.span?.gargoyle;
  if (!G || w.done >= 0) return;
  if (p.perch.length) p.perch = p.perch.filter((x) => s.enemies.some((e) => e.id === x.id && e.hp > 0));
  if (s.waveT >= p.gar) {
    p.gar = s.waveT + G.every + rngRange(s.rng, -24, 24);
    // A plank by the stretcher, ahead of it (the bridge: under it too), whole and nobody on it.
    const here = r.walk ? imax(0, walkPlankAt(r, w.x, w.y)) : imax(-1, bridgePlank(r, w.y));
    const from = r.walk ? (here > 0 ? here + 1 : nextPlank(r, w)) : here;
    const can: number[] = [];
    for (let k = from; k < from + 3 && k < p.torn.length; k++) if (k >= 0 && !p.torn[k] && !p.perch.some((x) => x.plank === k)) can.push(k);
    const k = can.length ? (can[rngInt(s.rng, can.length)] as number) : -1;
    const q = k >= 0 ? plankPoint(r, k) : null;
    if (q) {
      const side = rngInt(s.rng, 2) === 0 ? -1 : 1;
      const g = scaled(s, spawnEnemy(s, 'gargoyle', 1, false, 0, q.x + (r.walk ? 3200 : side * 3200), q.y - 900));
      p.perch.push({ id: g.id, plank: k, t: -1 });
    }
  }
  // Laying a torn plank again: the hero standing still by it.
  if (!h || h.moving) {
    p.mend = -1;
    p.mendT = 0;
    return;
  }
  const M = r.walk ?? r.span;
  if (!M) return;
  let best = -1;
  let bd = NEVER;
  // (The plank under the stretcher first, if it is in reach: it is the one it is hanging over.)
  const under = r.span ? bridgePlank(r, w.y) : walkPlankAt(r, w.x, w.y);
  const uq = under >= 0 && p.torn[under] ? plankPoint(r, under) : null;
  if (uq && withinRange(h.x, h.y, uq.x, uq.y, M.mendReach)) {
    best = under;
    bd = -1;
  }
  for (let k = 0; k < p.torn.length && bd >= 0; k++) {
    if (!p.torn[k]) continue;
    const q = plankPoint(r, k);
    if (!q) continue;
    const d = dist2(h.x, h.y, q.x, q.y);
    if (d <= M.mendReach * M.mendReach && d < bd) {
      bd = d;
      best = k;
    }
  }
  if (best !== p.mend) {
    p.mend = best;
    p.mendT = 0;
  }
  if (best < 0) return;
  if (++p.mendT >= M.mend) {
    p.torn[best] = 0;
    p.mend = -1;
    p.mendT = 0;
    const q = plankPoint(r, best);
    ev(s, { t: 'senda', kind: 'plank', npc: '', x: q?.x ?? 0, y: q?.y ?? 0, n: best });
  }
};

/** The walkway's next plank ahead of a stretcher on a landing. */
const nextPlank = (r: LitterRules, w: LitterState): number => {
  const W = r.walk;
  if (!W) return 0;
  let n = 0;
  for (let i = 0; i < W.spans.length; i++) {
    if (w.seg <= i * 5) return n;
    n += (W.spans[i] as unknown[]).length;
  }
  return n;
};

export const pasTick = (s: BattleState, r: LitterRules, w: LitterState, h: Hero | undefined, cue: (c: string) => void): void => {
  if (!r.ridge && !r.walk && !r.span) return;
  const p = (w.pas ??= fresh(r));
  if (r.ridge) ridgeTick(s, r, w, p, h, cue);
  if (r.walk) walkTick(s, r, w, p, h, cue);
  if (r.span) bridgeTick(s, r, w, p, h, cue);
  planks(s, r, w, p, h);
};

/** Its own nightmares (true: handled here): the slingers on their ledge, the mangonel, the gargoyles going to a plank. */
export const pasHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const p = w?.pas;
  if (!r || !w || !p) return false;
  if (r.ridge && e.kind === 'slinger') {
    // Along their ledge, level with the stretcher; winding up while a volley is coming.
    const k = (e.id - p.boss) % 3;
    stepToward(e, r.ridge.ledge + k * 300, w.y - 1600 + k * 1600, px(e));
    e.facing = 512;
    e.anim = p.marks.length ? 'attack' : 'idle';
    return true;
  }
  if (r.span && e.id === p.boss) {
    e.facing = 256;
    if (e.anim === 'attack' && s.waveT >= p.stone - (r.span.mangonel.every - 10)) e.anim = 'idle';
    return true;
  }
  if (p.perch.length) {
    const x = p.perch.find((o) => o.id === e.id);
    if (x) {
      const q = x.plank >= 0 ? plankPoint(r, x.plank) : person(s, 'piet');
      if (!q || (x.plank < 0 && p.slip < 0)) {
        p.perch = p.perch.filter((o) => o.id !== e.id);
        return false;
      }
      if (x.t < 0) {
        e.anim = 'walk';
        if (stepToward(e, q.x + (x.plank < 0 ? 350 : 0), q.y - (x.plank < 0 ? 250 : 0), px(e)) || withinRange(e.x, e.y, q.x, q.y, 400)) {
          x.t = s.tick;
          if (x.plank >= 0) ev(s, { t: 'senda', kind: 'perch', npc: '', x: q.x, y: q.y, n: x.plank });
        }
        return true;
      }
      e.anim = 'attack';
      const G = r.walk?.gargoyle ?? r.span?.gargoyle;
      if (x.plank >= 0 && G && s.tick - x.t >= G.tear) {
        // (A plank already gone: nothing to tear, it flies off.)
        if (!p.torn[x.plank]) {
          p.torn[x.plank] = 1;
          ev(s, { t: 'senda', kind: 'tear', npc: '', x: q.x, y: q.y, n: x.plank });
        }
        p.perch = p.perch.filter((o) => o.id !== e.id);
      }
      return true;
    }
  }
  return false;
};

/** Nightmares that don't strike the stretcher by being near it (they keep to their own work). */
export const pasBusy = (s: BattleState, e: Enemy): boolean => {
  const r = s.rules?.senda?.litter;
  const p = s.senda?.litter?.pas;
  if (!r || !p) return false;
  return (r.ridge !== null && e.kind === 'slinger') || e.id === p.boss || p.perch.some((x) => x.id === e.id);
};

/** Lost by its own rule: Piet let go (the walkway), the stretcher down a gap (the bridge). */
export const pasResult = (s: BattleState): 'lost' | null => {
  const r = s.rules?.senda?.litter;
  const p = s.senda?.litter?.pas;
  if (!r || !p) return null;
  if (r.walk && p.slip === -3) return 'lost';
  if (r.span && p.hang >= r.span.hang) return 'lost';
  return null;
};
